// Page Interview server functions.
// Admin fns manage the people directory, generate respondent-aware interview
// questions from an existing page, save/send interviews (many per page),
// generate follow-ups, build an AI draft from one or more completed interviews,
// and apply approved rewrites back onto the page.
// Public token-scoped fns let the respondent answer from their phone.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type PageInterviewQuestion = { id: string; question_text: string; order: number };

export type PageInterviewStatus =
  | "draft"
  | "ready"
  | "sent"
  | "in_progress"
  | "completed"
  | "reviewed"
  | "applied"
  // legacy values still present on older rows
  | "awaiting_ai_draft"
  | "pending_approval";

export const RELATIONSHIP_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "founder", label: "Founder" },
  { value: "owner", label: "Owner" },
  { value: "employee", label: "Employee" },
  { value: "former_employee", label: "Former employee" },
  { value: "customer", label: "Customer" },
  { value: "builder", label: "Builder or contractor" },
  { value: "vendor", label: "Vendor" },
  { value: "industry_expert", label: "Industry expert" },
  { value: "other", label: "Other" },
];

export type InterviewPerson = {
  id: string;
  full_name: string;
  role_title: string;
  relationship: string;
  email: string | null;
  phone: string | null;
  is_builtin: boolean;
};

export type PageInterviewSession = {
  id: string;
  page_slug: string;
  page_title: string;
  focus_note: string;
  speaker: string;
  speaker_role: string;
  speaker_relationship: string;
  person_id: string | null;
  length_mode: string;
  parent_session_id: string | null;
  questions: PageInterviewQuestion[];
  interview_token: string | null;
  status: PageInterviewStatus;
  created_at: string;
  sent_at: string | null;
  completed_at: string | null;
  reviewed_at: string | null;
  used_in_content_at: string | null;
  answered_count?: number;
};

export type PageInterviewDraft = {
  id: string;
  session_id: string | null;
  page_slug: string;
  section_rewrites: Record<string, { text: string; note: string }>;
  proposed_sections: Array<{ heading: string; body: string; why: string }>;
  quotes: Array<{ text: string; attribution: string; section_key: string }>;
  conflicts: Array<{ topic: string; detail: string }>;
  needs_verification: Array<{ claim: string; source: string }>;
  source_session_ids: string[];
  approvals: Record<string, boolean>;
  status: string;
  created_at: string;
};

async function ensureAdmin(supabase: any, userId: string) {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!data) throw new Error("Forbidden");
}

async function getAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin as any;
}

function genToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function loadPageContext(sb: any, slug: string) {
  const { data: page } = await sb
    .from("pages")
    .select("slug, kind, title, meta_title, meta_description, sections")
    .eq("slug", slug)
    .maybeSingle();
  if (!page) throw new Error(`Page /${slug} not found`);
  return {
    slug: page.slug,
    kind: page.kind,
    title: page.title,
    meta_title: page.meta_title,
    meta_description: page.meta_description,
    sections: (page.sections ?? {}) as Record<string, string>,
  };
}

function respondentOf(session: any) {
  return {
    name: (session.speaker ?? "Tim Forstie") as string,
    role: (session.speaker_role ?? "Owner") as string,
    relationship: (session.speaker_relationship ?? "owner") as string,
  };
}

const respondentSchema = z.object({
  speaker: z.string().min(2).max(120),
  speaker_role: z.string().max(120).default(""),
  speaker_relationship: z.string().max(60).default("owner"),
});

const questionsToRows = (questions: string[]): PageInterviewQuestion[] =>
  questions.map((q, i) => ({
    id: `q${i + 1}-${crypto.randomUUID().slice(0, 8)}`,
    question_text: q,
    order: i,
  }));

/* ============ PEOPLE DIRECTORY ============ */

export const adminListInterviewPeople = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<InterviewPerson[]> => {
    await ensureAdmin(context.supabase, context.userId);
    const { data, error } = await (context.supabase as any)
      .from("interview_people")
      .select("*")
      .order("is_builtin", { ascending: false })
      .order("full_name");
    if (error) throw new Error(error.message);
    return (data ?? []) as InterviewPerson[];
  });

export const adminSaveInterviewPerson = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid().optional(),
        full_name: z.string().min(2).max(120),
        role_title: z.string().max(120).default(""),
        relationship: z.string().max(60).default("other"),
        email: z.string().max(200).optional().default(""),
        phone: z.string().max(60).optional().default(""),
      })
      .parse(i),
  )
  .handler(async ({ data, context }): Promise<InterviewPerson> => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const payload = {
      full_name: data.full_name.trim(),
      role_title: data.role_title.trim(),
      relationship: data.relationship,
      email: data.email?.trim() || null,
      phone: data.phone?.trim() || null,
    };
    const q = data.id
      ? sb.from("interview_people").update(payload).eq("id", data.id)
      : sb.from("interview_people").insert(payload);
    const { data: row, error } = await q.select("*").single();
    if (error) throw new Error(error.message);
    return row as InterviewPerson;
  });

export const adminDeleteInterviewPerson = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { error } = await (context.supabase as any)
      .from("interview_people")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* ============ ADMIN — interviews ============ */

export const adminListPageInterviews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PageInterviewSession[]> => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { data: sessions, error } = await sb
      .from("page_interview_sessions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const { data: counts } = await sb
      .from("page_interview_answers")
      .select("session_id, transcript, clarification");
    const tally: Record<string, number> = {};
    for (const r of counts ?? []) {
      const answered = `${r.transcript ?? ""}${r.clarification ?? ""}`.trim().length > 0;
      if (answered) tally[r.session_id] = (tally[r.session_id] ?? 0) + 1;
    }
    return (sessions ?? []).map((s: any) => ({ ...s, answered_count: tally[s.id] ?? 0 }));
  });

/** Analyze the page and propose interview questions (nothing saved yet). */
export const adminGeneratePageQuestions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        focus_note: z.string().max(2000).default(""),
        count: z.number().int().min(1).max(15).default(6),
      })
      .merge(respondentSchema)
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const page = await loadPageContext(context.supabase, data.slug);
    const { generatePageQuestions } = await import("@/lib/page-interview.server");
    const r = await generatePageQuestions(
      page,
      data.focus_note,
      {
        name: data.speaker,
        role: data.speaker_role,
        relationship: data.speaker_relationship,
      },
      data.count,
    );
    if (!r.ok) return { ok: false as const, error: r.error };
    return { ok: true as const, questions: r.questions, page_title: page.title };
  });

/** Swap one question for a fresh alternative. */
export const adminRegeneratePageQuestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        focus_note: z.string().max(2000).default(""),
        current: z.string().max(2000).default(""),
        others: z.array(z.string().max(2000)).max(20).default([]),
      })
      .merge(respondentSchema)
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const page = await loadPageContext(context.supabase, data.slug);
    const { regenerateSingleQuestion } = await import("@/lib/page-interview.server");
    const r = await regenerateSingleQuestion(
      page,
      data.focus_note,
      {
        name: data.speaker,
        role: data.speaker_role,
        relationship: data.speaker_relationship,
      },
      data.current,
      data.others,
    );
    if (!r.ok) return { ok: false as const, error: r.error };
    return { ok: true as const, question: r.question };
  });

/**
 * Create or update an interview WITHOUT sending it. Never overwrites another
 * interview for the same page — a new call with no id always inserts.
 */
export const adminSavePageInterview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid().optional(),
        slug: z.string().min(1),
        page_title: z.string().max(300).default(""),
        focus_note: z.string().max(2000).default(""),
        person_id: z.string().uuid().optional().nullable(),
        length_mode: z.enum(["quick", "standard", "custom", "followup"]).default("standard"),
        parent_session_id: z.string().uuid().optional().nullable(),
        questions: z.array(z.string().min(3)).min(1).max(15),
        ready: z.boolean().default(false),
      })
      .merge(respondentSchema)
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const base = {
      page_slug: data.slug,
      page_title: data.page_title,
      focus_note: data.focus_note,
      speaker: data.speaker,
      speaker_role: data.speaker_role,
      speaker_relationship: data.speaker_relationship,
      person_id: data.person_id ?? null,
      length_mode: data.length_mode,
      parent_session_id: data.parent_session_id ?? null,
      questions: questionsToRows(data.questions),
      status: data.ready ? "ready" : "draft",
    };
    const q = data.id
      ? sb.from("page_interview_sessions").update(base).eq("id", data.id)
      : sb.from("page_interview_sessions").insert(base);
    const { data: row, error } = await q.select("id, status").single();
    if (error) throw new Error(error.message);
    return { id: row.id as string, status: row.status as PageInterviewStatus };
  });

/** Mint the public token and mark the interview sent. */
export const adminSendPageInterview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { data: existing } = await sb
      .from("page_interview_sessions")
      .select("id, interview_token, status")
      .eq("id", data.id)
      .maybeSingle();
    if (!existing) throw new Error("Interview not found");
    const token = existing.interview_token || genToken();
    const patch: any = { interview_token: token };
    if (["draft", "ready"].includes(existing.status)) {
      patch.status = "sent";
      patch.sent_at = new Date().toISOString();
    }
    const { error } = await sb.from("page_interview_sessions").update(patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { interview_token: token as string };
  });

/** Generate up to 3 follow-up questions from a completed interview. */
export const adminGenerateFollowUp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ session_id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { data: session } = await sb
      .from("page_interview_sessions")
      .select("*")
      .eq("id", data.session_id)
      .maybeSingle();
    if (!session) return { ok: false as const, error: "Interview not found" };

    const page = await loadPageContext(sb, session.page_slug);
    const { data: answers } = await sb
      .from("page_interview_answers")
      .select("question_text, transcript, clarification")
      .eq("session_id", data.session_id)
      .order("created_at");

    const { generateFollowUpQuestions } = await import("@/lib/page-interview.server");
    const r = await generateFollowUpQuestions(
      page,
      session.focus_note ?? "",
      respondentOf(session),
      (answers ?? []).map((a: any) => ({
        question: a.question_text,
        answer: [a.transcript, a.clarification].filter(Boolean).join("\n"),
      })),
    );
    if (!r.ok) return { ok: false as const, error: r.error };
    return {
      ok: true as const,
      questions: r.questions,
      page_slug: session.page_slug as string,
      page_title: session.page_title as string,
      focus_note: (session.focus_note ?? "") as string,
      person_id: (session.person_id ?? null) as string | null,
      speaker: session.speaker as string,
      speaker_role: (session.speaker_role ?? "") as string,
      speaker_relationship: (session.speaker_relationship ?? "owner") as string,
    };
  });

export const adminDeletePageInterview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { error } = await sb.from("page_interview_sessions").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminMarkInterviewReviewed = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { error } = await (context.supabase as any)
      .from("page_interview_sessions")
      .update({ status: "reviewed", reviewed_at: new Date().toISOString() })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminGetPageInterview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { data: session } = await sb
      .from("page_interview_sessions")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (!session) return null;
    const { data: answers } = await sb
      .from("page_interview_answers")
      .select("*")
      .eq("session_id", data.id)
      .order("created_at");
    const { data: siblings } = await sb
      .from("page_interview_sessions")
      .select("*")
      .eq("page_slug", session.page_slug)
      .order("created_at", { ascending: false });
    const { data: draft } = await sb
      .from("page_interview_drafts")
      .select("*")
      .eq("page_slug", session.page_slug)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const { data: page } = await sb
      .from("pages")
      .select("sections")
      .eq("slug", session.page_slug)
      .maybeSingle();

    const answerCounts: Record<string, number> = {};
    const { data: allAnswers } = await sb
      .from("page_interview_answers")
      .select("session_id, transcript, clarification");
    for (const a of allAnswers ?? []) {
      if (`${a.transcript ?? ""}${a.clarification ?? ""}`.trim())
        answerCounts[a.session_id] = (answerCounts[a.session_id] ?? 0) + 1;
    }

    return {
      session: session as PageInterviewSession,
      siblings: (siblings ?? []).map((s: any) => ({
        ...s,
        answered_count: answerCounts[s.id] ?? 0,
      })) as PageInterviewSession[],
      answers: (answers ?? []) as Array<{
        id: string;
        question_id: string;
        question_text: string;
        transcript: string;
        clarification: string;
        audio_url: string | null;
        skipped: boolean;
      }>,
      draft: (draft ?? null) as PageInterviewDraft | null,
      current_sections: (page?.sections ?? {}) as Record<string, string>,
    };
  });

/** Admin-side answer entry (typed, or corrections to a transcription). */
export const adminSavePageAnswer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        session_id: z.string().uuid(),
        question_id: z.string().min(1),
        question_text: z.string().min(1).max(2000),
        transcript: z.string().max(20000),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    const { error } = await sb.from("page_interview_answers").upsert(
      {
        session_id: data.session_id,
        question_id: data.question_id,
        question_text: data.question_text,
        transcript: data.transcript,
      },
      { onConflict: "session_id,question_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

async function buildDraft(sb: any, slug: string, sessionIds: string[]) {
  const page = await loadPageContext(sb, slug);
  const { data: sessions } = await sb
    .from("page_interview_sessions")
    .select("*")
    .in("id", sessionIds);
  if (!sessions?.length) return { ok: false as const, error: "No interviews selected." };

  const { data: answers } = await sb
    .from("page_interview_answers")
    .select("session_id, question_text, transcript, clarification")
    .in("session_id", sessionIds)
    .order("created_at");

  const sources = sessions.map((s: any) => ({
    speaker: s.speaker ?? "Tim Forstie",
    role: s.speaker_role ?? "Owner",
    relationship: s.speaker_relationship ?? "owner",
    qa: (answers ?? [])
      .filter((a: any) => a.session_id === s.id)
      .map((a: any) => ({
        question: a.question_text,
        answer: [a.transcript, a.clarification].filter((x: string) => x && x.trim()).join("\n"),
      })),
  }));

  const focusNote = sessions.map((s: any) => s.focus_note).filter(Boolean).join("\n");
  const { generatePageDraft } = await import("@/lib/page-interview.server");
  const r = await generatePageDraft(page, focusNote, sources);
  if (!r.ok) return { ok: false as const, error: r.error };

  const { data: row, error } = await sb
    .from("page_interview_drafts")
    .insert({
      session_id: sessionIds[0],
      page_slug: slug,
      section_rewrites: r.draft.section_rewrites,
      proposed_sections: r.draft.proposed_sections,
      quotes: r.draft.quotes,
      conflicts: r.draft.conflicts,
      needs_verification: r.draft.needs_verification,
      source_session_ids: sessionIds,
      approvals: {},
      status: "pending_approval",
    })
    .select("*")
    .single();
  if (error) throw new Error(error.message);

  await sb
    .from("page_interview_sessions")
    .update({ status: "reviewed", reviewed_at: new Date().toISOString() })
    .in("id", sessionIds)
    .in("status", ["completed", "awaiting_ai_draft", "pending_approval", "in_progress"]);

  return { ok: true as const, draft: row as PageInterviewDraft };
}

/** Build a draft from one or more completed interviews for the same page. */
export const adminGeneratePageDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        session_ids: z.array(z.string().uuid()).min(1).max(12),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;
    // Clear older drafts for this page so the admin only reviews the newest.
    await sb.from("page_interview_drafts").delete().eq("page_slug", data.slug);
    return await buildDraft(sb, data.slug, data.session_ids);
  });

/** Apply the approved section rewrites onto the live page. */
export const adminApplyPageDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        session_ids: z.array(z.string().uuid()).default([]),
        sections: z.record(z.string()),
        mark_humanized: z.boolean().default(true),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase as any;

    const { data: page } = await sb
      .from("pages")
      .select("sections")
      .eq("slug", data.slug)
      .maybeSingle();
    const merged = { ...((page?.sections ?? {}) as Record<string, string>), ...data.sections };

    const patch: any = { sections: merged };
    if (data.mark_humanized) patch.humanized_at = new Date().toISOString();
    const { error } = await sb.from("pages").update(patch).eq("slug", data.slug);
    if (error) throw new Error(error.message);

    await sb
      .from("page_interview_drafts")
      .update({
        status: "applied",
        approvals: Object.fromEntries(Object.keys(data.sections).map((k) => [k, true])),
      })
      .eq("page_slug", data.slug);

    if (data.session_ids.length) {
      await sb
        .from("page_interview_sessions")
        .update({ status: "applied", used_in_content_at: new Date().toISOString() })
        .in("id", data.session_ids);
    }

    return { ok: true, slug: data.slug };
  });

/* ============ PUBLIC (token-validated) ============ */

export const getPageInterviewByToken = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => z.object({ token: z.string().min(8).max(200) }).parse(i))
  .handler(async ({ data }) => {
    const sb = await getAdmin();
    const { data: session } = await sb
      .from("page_interview_sessions")
      .select("*")
      .eq("interview_token", data.token)
      .maybeSingle();
    if (!session || session.status === "applied") return null;
    const { data: answers } = await sb
      .from("page_interview_answers")
      .select("question_id, transcript, clarification")
      .eq("session_id", session.id);
    return {
      session: {
        id: session.id as string,
        page_title: (session.page_title || `/${session.page_slug}`) as string,
        page_slug: session.page_slug as string,
        speaker: (session.speaker ?? "Tim Forstie") as string,
        questions: (session.questions ?? []) as PageInterviewQuestion[],
        status: session.status as PageInterviewStatus,
      },
      answers: (answers ?? []) as Array<{
        question_id: string;
        transcript: string;
        clarification: string;
      }>,
    };
  });

export const savePageInterviewAnswer = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) =>
    z
      .object({
        token: z.string().min(8).max(200),
        question_id: z.string().min(1),
        question_text: z.string().min(1).max(2000),
        transcript: z.string().max(20000),
        clarification: z.string().max(20000).default(""),
        skipped: z.boolean().default(false),
        audio_url: z.string().url().optional().nullable(),
      })
      .parse(i),
  )
  .handler(async ({ data }) => {
    const sb = await getAdmin();
    const { data: session } = await sb
      .from("page_interview_sessions")
      .select("id, status")
      .eq("interview_token", data.token)
      .maybeSingle();
    if (!session || session.status === "applied") throw new Error("Invalid link");

    const { error } = await sb.from("page_interview_answers").upsert(
      {
        session_id: session.id,
        question_id: data.question_id,
        question_text: data.question_text,
        transcript: data.transcript,
        clarification: data.clarification,
        skipped: data.skipped,
        audio_url: data.audio_url ?? null,
      },
      { onConflict: "session_id,question_id" },
    );
    if (error) throw new Error(error.message);

    if (["sent", "draft", "ready"].includes(session.status)) {
      await sb.from("page_interview_sessions").update({ status: "in_progress" }).eq("id", session.id);
    }
    return { ok: true };
  });

/** Respondent finishes — partial answers are fine. Draft is left to the admin. */
export const completePageInterview = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => z.object({ token: z.string().min(8).max(200) }).parse(i))
  .handler(async ({ data }) => {
    const sb = await getAdmin();
    const { data: session } = await sb
      .from("page_interview_sessions")
      .select("id, status")
      .eq("interview_token", data.token)
      .maybeSingle();
    if (!session || session.status === "applied") throw new Error("Invalid link");

    await sb
      .from("page_interview_sessions")
      .update({ status: "completed", completed_at: new Date().toISOString() })
      .eq("id", session.id);

    return { ok: true };
  });
